import { useTranslation, Trans } from 'react-i18next';
import { Stack, Button, Box, Text } from '@mantine/core';
import { SectionContentProps } from '../types';
import RedactionChip from '@app/features/Redaction/RedactionChip';
import dialogStoreVar from '@app/features/Dialog/store/dialogStore';
import { useReactiveVar, ApolloError } from '@apollo/client';
import { palette } from '@app/shared/ui-kit/colors';
import {
  useRemoveRedactionsMutation,
  DeleteAction,
  GetOneResourceDocument,
} from '@app/types/blackroom-api';
import invokePromptDelete from '@features/Prompt/PromptDelete';
import { FONT_WEIGHT } from '@app/shared/ui-kit/fonts';
import { showNotification } from '@mantine/notifications';
import { isRedactionRemovalAllowed } from '@app/shared/constants/resource';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function SectionRedact({ resource }: SectionContentProps) {
  const { t } = useTranslation();
  const dialogStore = useReactiveVar(dialogStoreVar);

  const [removeRedactions] = useRemoveRedactionsMutation();

  // Only allow redaction removal for documents uploaded after the cutoff date
  const canRemoveRedactions = isRedactionRemovalAllowed(resource.createdAt);

  /* ******************************** CALLBACKS ******************************* */

  const openRedactionDialog = () => {
    dialogStore?.invokeDialog?.('dialog-redaction', {
      modalProps: {
        title: t('Redaction:DialogRedaction.title'),
      },
      resourcesToRedact: [resource],
    });
  };

  const openDeleteRedactionDialog = (
    redaction: NonNullable<typeof resource.redactions>[number],
  ) => {
    const occurrenceCount = redaction.matchData?.length || 0;
    const isManual = redaction.entity === 'manual';

    invokePromptDelete({
      componentProps: {
        deleteAction: DeleteAction.Remove,
        modalContent: (
          <Text
            size="lg"
            ta="center"
            fw={FONT_WEIGHT.bold}
            c={palette.blackroomRed}
          >
            <Trans
              i18nKey={
                isManual
                  ? 'Redaction:SectionRedact.deleteConfirmationManual'
                  : 'Redaction:SectionRedact.deleteConfirmation'
              }
              count={occurrenceCount}
              values={{
                text: redaction.text,
              }}
              components={{
                strong: <Text component="span" fw={FONT_WEIGHT.bolder} />,
              }}
            />
          </Text>
        ),
      },
      onConfirm: async () => {
        try {
          await removeRedactions({
            variables: {
              redactionIds: [redaction.id],
              workspaceId: resource.workspaceId,
            },
            refetchQueries: [
              {
                query: GetOneResourceDocument,
                variables: {
                  workspaceId: resource.workspaceId,
                  resourceId: resource.id,
                },
              },
            ],
          });
        } catch (error: unknown) {
          if (error instanceof ApolloError) {
            showNotification({
              title: t('Common:errors.genericTitle'),
              message: error.message,
              color: 'blackroomRed',
            });
          }
          console.error(error);
        }
      },
    });
  };

  /* ******************************** RENDERING ******************************* */

  return (
    <Stack px="md" gap={20} w="100%">
      <Stack gap={10} align="stretch" pb={5}>
        <Box ta="center" fz={11} c={palette.blackroomGray}>
          {t('Redaction:RedactionTable.columns.redactions.redactionCount', {
            count: resource.redactions?.length,
          })}
        </Box>
        <Button color="blackroomBlack" onClick={() => openRedactionDialog()}>
          {t('Redaction:DialogRedaction.title')}
        </Button>
      </Stack>

      {resource.redactions?.length ? (
        <Stack gap={5} align="stretch" w="100%">
          {resource.redactions?.map((redaction) => (
            <RedactionChip
              key={redaction.id}
              redaction={redaction}
              withPreview
              onDelete={
                canRemoveRedactions
                  ? () => openDeleteRedactionDialog(redaction)
                  : undefined
              }
              w="100%"
              fill
            />
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}

export default SectionRedact;
